import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { RootState } from '.';

import * as API from '../services/api';
import { setScreensavers, reloadWebView } from '../utils/NativeWebView';
import { Section, Location, Screensaver, MessageData } from '../types';

export interface WebSettingsState {
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  refetching: 'idle' | 'pending' | 'succeeded' | 'failed';
  jsonVersion: number;
  sections: Section [];
  screensavers?: Screensaver [];
  locations: Location [];
  modalChat: MessageData;
  showSettingsInfo: boolean;
}

const initialState = {
  jsonVersion: 0.0,
  loading: 'idle',
  refetching: 'idle',
  sections: [],
  locations: [],
  modalChat: {},
  showSettingsInfo: false,
} as WebSettingsState;

export const getSettings = createAsyncThunk(
  //action type string
  'webSettings/getSettings',
  async () => {
    const response = await API.getJson();
    return response;
  }
);

export const refetchData = createAsyncThunk(
  //action type string
  'webSettings/refetchData',
  async () => {
    const response = await API.getJson();
    return response;
  }
);

const webSettingsSlice = createSlice({
  initialState,
  name: 'webSettings',
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSettings.fulfilled, (state, action) => {
      state.jsonVersion = action.payload.app.jsonVersion;

      state.screensavers = action.payload.app.screensavers;
      setScreensavers(action.payload.app.screensavers);

      state.sections = action.payload.sections;
      state.locations = action.payload.locations;
      state.modalChat = action.payload.modalChat;

      state.showSettingsInfo = window.showSettingsInfo || action.payload.app.devDevices?.includes(window.deviceID);

      state.loading = 'succeeded';
    }),
    builder.addCase(getSettings.rejected, (state) => {
      state.loading = 'failed';
    }),
    builder.addCase(getSettings.pending, (state) => {
      state.loading = 'pending';
    }),
    builder.addCase(refetchData.fulfilled, (state, action) => {
      if (action.payload.app.jsonVersion !== state.jsonVersion) {
        // reload webview
        reloadWebView();
      }

      if(JSON.stringify(state.screensavers) !== JSON.stringify(action.payload.app.screensavers)) {
        state.screensavers = action.payload.app.screensavers;
        setScreensavers(action.payload.app.screensavers);
      }

      if(state.sections !== action.payload.sections) state.sections = action.payload.sections;
      if(state.locations !== action.payload.locations) state.locations = action.payload.locations;

      state.showSettingsInfo = window?.showSettingsInfo || action.payload.app.devDevices?.includes(window.deviceID);

      state.refetching = 'succeeded';
    }),
    builder.addCase(refetchData.rejected, (state) => {
      state.refetching = 'failed';
    }),
    builder.addCase(refetchData.pending, (state) => {
      state.refetching = 'pending';
    });
  },

});

const { reducer } = webSettingsSlice;

// export const { startLoading } = actions;

// export const selectSectionById = (state: RootState, sectionID:string) => {
//   const { sections } = state.webSettings;
//   return sections.find(section => section.id === sectionID);
// };

export const selectSectionById = createSelector(
  (state:RootState) => state.webSettings.sections,
  // Take the second arg, `id`, and forward to the output selector
  (state: RootState, id:string) => id,
  // Output selector gets (`sections, id)` as args
  (sections, id) => sections.find(section => section.id === id)
);

export default reducer;
